import { supabase } from './supabase';

export interface TwilioSettings {
  businessId: string;
  accountSid: string;
  authToken: string;
  phoneNumber: string;
  callForwardingEnabled?: boolean;
  forwardingNumber?: string;
  resourcePhoneNumbers?: string[];
  enabled: boolean;
}


/**
 * Formats a phone number to E.164 format
 */
export function formatToE164(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Add + prefix if not present
  if (!phoneNumber.startsWith('+')) {
    // Assume US number if no country code
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    }
    return `+${cleaned}`;
  }
  
  return phoneNumber;
}

/**
 * Fetches new messages from Twilio and stores them in the database
 */
export async function fetchAndStoreMessages(settings: {
  businessId: string;
  accountSid: string;
  authToken: string;
  phoneNumber: string;
}): Promise<void> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return;
  }

  try {
    // Get the latest message date from our database
    const { data: latestMessage } = await supabase
      .from('sms_messages')
      .select('created_at')
      .eq('business_id', settings.businessId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    // Format the date for Twilio's API (if we have a latest message)
    const dateFilter = latestMessage
      ? new Date(latestMessage.created_at).toISOString()
      : new Date(Date.now() - 60 * 60 * 1000).toISOString(); // Last 1 hour if no messages (reduced from 24 hours)

    console.log(`Fetching Twilio messages since: ${dateFilter}`);

    // Fetch messages from Twilio
    const credentials = btoa(`${settings.accountSid}:${settings.authToken}`);
    const response = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${settings.accountSid}/Messages.json?To=${settings.phoneNumber}&DateSent>=${dateFilter}`,
      {
        headers: {
          'Authorization': `Basic ${credentials}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch messages from Twilio');
    }

    const result = await response.json();
    
    // Filter out messages we already have
    const { data: existingMessages } = await supabase
      .from('sms_messages')
      .select('twilio_message_sid')
      .eq('business_id', settings.businessId);

    const existingSids = new Set(existingMessages?.map(m => m.twilio_message_sid) || []);

    // Store new messages
    const newMessages = result.messages.filter(
      (msg: any) => !existingSids.has(msg.sid)
    );

    if (newMessages.length > 0) {
      const { error } = await supabase
        .from('sms_messages')
        .insert(
          newMessages.map((msg: any) => ({
            business_id: settings.businessId,
            from_number: msg.from,
            to_number: msg.to,
            body: msg.body,
            direction: msg.direction === 'inbound' ? 'inbound' : 'outbound',
            twilio_message_sid: msg.sid,
            created_at: new Date(msg.date_sent).toISOString(),
          }))
        );

      if (error) {
        console.error('Error storing messages:', error);
      }
    }
  } catch (error) {
    console.error('Error fetching messages:', error);
  }
}

/**
 * Updates Twilio settings for a business
 */
export async function updateTwilioSettings({
  businessId,
  accountSid,
  authToken,
  phoneNumber,
  resourcePhoneNumbers = [],
  enabled,
}: TwilioSettings): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    // If disabling Twilio, clear the settings
    if (!enabled) {
      const { error } = await supabase
        .from('businesses')
        .update({
          twilio_enabled: false,
          twilio_account_sid: '',
          twilio_auth_token: '',
          VITE_TWILIO_PHONE_NUMBER: '',
          VITE_TWILIO_RESOURCE_PHONE_NUMBERS: [],
          twilio_status: null,
          twilio_error_message: null
        })
        .eq('id', businessId);

      if (error) throw error;

      return {
        success: true,
      };
    }

    // Store credentials and immediately fetch messages
    const { error } = await supabase
      .from('businesses')
      .update({
        twilio_enabled: true,
        twilio_account_sid: accountSid,
        twilio_auth_token: authToken,
        VITE_TWILIO_PHONE_NUMBER: phoneNumber,
        VITE_TWILIO_RESOURCE_PHONE_NUMBERS: resourcePhoneNumbers,
        twilio_status: 'active',
        twilio_error_message: null
      })
      .eq('id', businessId);

    if (error) throw error;

    // Fetch initial messages
    await fetchAndStoreMessages({
      businessId,
      accountSid,
      authToken,
      phoneNumber: formatToE164(phoneNumber),
    });

    return {
      success: true,
    };

  } catch (error) {
    console.error('Error updating Twilio settings:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update Twilio settings',
    };
  }
}

/**
 * Gets Twilio settings for a business
 */
export async function getTwilioSettings(businessId: string): Promise<{ success: boolean; settings?: any; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    const { data: settings, error } = await supabase
      .from('businesses')
      .select('twilio_enabled, twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER, VITE_TWILIO_RESOURCE_PHONE_NUMBERS, twilio_status, twilio_error_message, twilio_call_forwarding_enabled, twilio_forwarding_number, twilio_webhooks_configured, twilio_webhook_configured_at')
      .eq('id', businessId)
      .single();

    if (error) throw error;

    return {
      success: true,
      settings: settings,
    };
  } catch (error) {
    console.error('Error getting Twilio settings:', error);
    return {
      success: false,
      error: 'Failed to get Twilio settings',
    };
  }
}

/**
 * Sends a test SMS message using the stored Twilio credentials
 */
export async function sendTestSMS(
  businessId: string, 
  toPhoneNumber: string,
  customMessage?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    // Get the business's Twilio settings
    const { data: settings, error: settingsError } = await supabase
      .from('businesses')
      .select('twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER')
      .eq('id', businessId)
      .single();

    if (settingsError || !settings?.twilio_account_sid || !settings?.twilio_auth_token || !settings?.VITE_TWILIO_PHONE_NUMBER) {
      return {
        success: false,
        error: 'Twilio credentials not found. Please save your Twilio settings first.',
      };
    }

    // Format phone numbers to E.164 format
    const formattedToNumber = formatToE164(toPhoneNumber);
    const formattedFromNumber = formatToE164(settings.VITE_TWILIO_PHONE_NUMBER);
    
    // Use custom message if provided, otherwise use default test message
    const messageBody = customMessage || `This is a test message from your booking system. 📱\n\nTo test if this number can receive messages, please reply "TEST" to this message.\n\nTwilio number: ${formattedFromNumber}`;

    // Send test message using Twilio API
    const credentials = btoa(`${settings.twilio_account_sid}:${settings.twilio_auth_token}`);
    const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${settings.twilio_account_sid}/Messages.json`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        To: formattedToNumber,
        From: formattedFromNumber,
        Body: messageBody,
      }).toString(),
    });

    const result = await response.json();
    
    if (!response.ok || result.error_code || result.error_message) {
      return {
        success: false,
        error: result.error_message || result.message || 'Failed to send test message',
      };
    }

    // Store the outbound message in the database
    const { error: insertError } = await supabase
      .from('sms_messages')
      .insert({
        business_id: businessId,
        from_number: formattedFromNumber,
        to_number: formattedToNumber,
        body: messageBody,
        direction: 'outbound',
        twilio_message_sid: result.sid,
        created_at: new Date().toISOString(),
      });

    if (insertError) {
      console.error('Error storing outbound message:', insertError);
    }

    // Fetch any new messages (including the one we just sent)
    await fetchAndStoreMessages({
      businessId,
      accountSid: settings.twilio_account_sid,
      authToken: settings.twilio_auth_token,
      phoneNumber: formattedFromNumber,
    });

    return {
      success: true,
    };

  } catch (error) {
    console.error('Error sending test SMS:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send test message',
    };
  }
}

/**
 * Sends an SMS message using test credentials
 */
export async function sendSMSWithTestCredentials(
  businessId: string,
  toPhoneNumber: string,
  message: string,
  aiGenerated: boolean = false
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    // Get the business's Twilio settings
    const { data: settings, error: settingsError } = await supabase
      .from('businesses')
      .select('twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER')
      .eq('id', businessId)
      .single();

    if (settingsError || !settings?.twilio_account_sid || !settings?.twilio_auth_token || !settings?.VITE_TWILIO_PHONE_NUMBER) {
      console.error('Twilio settings error:', settingsError || 'Missing credentials');
      return {
        success: false,
        error: 'Twilio credentials not found. Please save your Twilio settings first.',
      };
    }

    // Format phone numbers to E.164 format
    const formattedToNumber = formatToE164(toPhoneNumber);
    const formattedFromNumber = formatToE164(settings.VITE_TWILIO_PHONE_NUMBER);

    console.log('Sending SMS with settings:', {
      to: formattedToNumber,
      from: formattedFromNumber,
      hasAccountSid: !!settings.twilio_account_sid,
      hasAuthToken: !!settings.twilio_auth_token,
      messageLength: message.length
    });

    // Send message using Twilio API
    const credentials = btoa(`${settings.twilio_account_sid}:${settings.twilio_auth_token}`);
    const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${settings.twilio_account_sid}/Messages.json`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        To: formattedToNumber,
        From: formattedFromNumber,
        Body: message,
      }).toString(),
    });

    const result = await response.json();
    
    if (!response.ok || result.error_code || result.error_message) {
      console.error('Twilio API error:', result);
      return {
        success: false,
        error: result.error_message || result.message || 'Failed to send message',
      };
    }

    // Store the outbound message in the all_messages table with AI tracking
    if (supabase) {
      const { error: insertError } = await supabase
        .from('all_messages')
        .insert({
          business_id: businessId,
          message_type: 'sms',
          direction: 'outbound',
          from_number: formattedFromNumber,
          to_number: formattedToNumber,
          content: message,
          ai_response: aiGenerated,  // Store AI flag
          read: 'read',  // Outbound messages are considered read
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (insertError) {
        console.error('Error storing outbound message:', insertError);
      }
      
      // Also store in legacy sms_messages table for backward compatibility
      const { error: legacyInsertError } = await supabase
        .from('sms_messages')
        .insert({
          business_id: businessId,
          from_number: formattedFromNumber,
          to_number: formattedToNumber,
          body: message,
          direction: 'outbound',
          twilio_message_sid: result.sid,
          created_at: new Date().toISOString(),
        });

      if (legacyInsertError) {
        console.error('Error storing legacy SMS message:', legacyInsertError);
      }
    }

    console.log('SMS sent successfully');
    return { success: true };

  } catch (error) {
    console.error('Error in sendSMSWithTestCredentials:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send message'
    };
  }
}

export async function validateTwilioSettingsForBusiness(businessId: string): Promise<{ 
  isValid: boolean; 
  error?: string;
  settings?: {
    twilio_enabled: boolean;
    twilio_status: string | null;
    twilio_account_sid: string | null;
    twilio_auth_token: string | null;
    VITE_TWILIO_PHONE_NUMBER: string | null;
  };
}> {
  try {
    if (!supabase) {
      return { isValid: false, error: 'Supabase client is not initialized' };
    }

    // Get the business's Twilio settings
    const { data: settings, error: settingsError } = await supabase
      .from('businesses')
      .select('twilio_enabled, twilio_status, twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER')
      .eq('id', businessId)
      .single();

    if (settingsError) {
      console.error('Error fetching Twilio settings:', settingsError);
      return { isValid: false, error: 'Failed to fetch Twilio settings' };
    }

    if (!settings) {
      return { isValid: false, error: 'Business not found' };
    }

    // Check if Twilio is enabled
    if (!settings.twilio_enabled) {
      return { isValid: false, error: 'Twilio is not enabled for this business', settings };
    }

    // Check if Twilio status is active
    if (settings.twilio_status !== 'active') {
      return { isValid: false, error: 'Twilio integration is not active', settings };
    }

    // Check if all required credentials are present
    if (!settings.twilio_account_sid || !settings.twilio_auth_token || !settings.VITE_TWILIO_PHONE_NUMBER) {
      return { isValid: false, error: 'Missing Twilio credentials', settings };
    }

    return { isValid: true, settings };
  } catch (error) {
    console.error('Error validating Twilio settings:', error);
    return { isValid: false, error: 'Failed to validate Twilio settings' };
  }
}

export async function sendSMS({ businessId, to, message }: { businessId: string; to: string; message: string }): Promise<{ success: boolean; error?: string }> {
  try {
    // First validate Twilio settings
    const validation = await validateTwilioSettingsForBusiness(businessId);
    if (!validation.isValid) {
      console.error('Twilio validation failed:', validation.error);
      return { success: false, error: validation.error };
    }

    const settings = validation.settings!;

    // Format phone numbers to E.164 format
    const formattedToNumber = formatToE164(to);
    const formattedFromNumber = formatToE164(settings.VITE_TWILIO_PHONE_NUMBER!);

    console.log('Sending SMS with settings:', {
      to: formattedToNumber,
      from: formattedFromNumber,
      hasAccountSid: !!settings.twilio_account_sid,
      hasAuthToken: !!settings.twilio_auth_token,
      messageLength: message.length
    });

    // Send message using Twilio API
    const credentials = btoa(`${settings.twilio_account_sid}:${settings.twilio_auth_token}`);
    const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${settings.twilio_account_sid}/Messages.json`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        To: formattedToNumber,
        From: formattedFromNumber,
        Body: message,
      }).toString(),
    });

    const result = await response.json();
    
    if (!response.ok || result.error_code || result.error_message) {
      console.error('Twilio API error:', result);
      return {
        success: false,
        error: result.error_message || result.message || 'Failed to send message',
      };
    }

    // Store the outbound message in the database
    if (supabase) {
      const { error: insertError } = await supabase
        .from('sms_messages')
        .insert({
          business_id: businessId,
          from_number: formattedFromNumber,
          to_number: formattedToNumber,
          body: message,
          direction: 'outbound',
          twilio_message_sid: result.sid,
          created_at: new Date().toISOString(),
        });

      if (insertError) {
        console.error('Error storing outbound message:', insertError);
      }
    }

    console.log('SMS sent successfully');
    return { success: true };

  } catch (error) {
    console.error('Error in sendSMS:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send message'
    };
  }
}

export async function validateTwilioClient(accountSid: string, authToken: string, phoneNumber: string): Promise<{ isValid: boolean; error?: string }> {
  try {
    const response = await fetch('/api/twilio/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accountSid,
        authToken,
        phoneNumber,
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      return { 
        isValid: false, 
        error: result.error || 'Failed to validate Twilio credentials' 
      };
    }

    return result;
  } catch (error) {
    console.error('Error validating Twilio client:', error);
    return { 
      isValid: false, 
      error: 'Failed to validate Twilio credentials' 
    };
  }
}

/**
 * Updates Twilio call forwarding settings
 */
export async function updateCallForwardingSettings({
  businessId,
  callForwardingEnabled,
  forwardingNumber
}: {
  businessId: string;
  callForwardingEnabled: boolean;
  forwardingNumber: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    // First get the current Twilio settings
    const { data: settings, error: settingsError } = await supabase
      .from('businesses')
      .select('twilio_enabled, twilio_status')
      .eq('id', businessId)
      .single();

    if (settingsError) {
      throw new Error('Failed to get current Twilio settings');
    }

    if (!settings.twilio_enabled || settings.twilio_status !== 'active') {
      return {
        success: false,
        error: 'Twilio must be enabled and active to configure call forwarding'
      };
    }

    // Update call forwarding settings
    const { error } = await supabase
      .from('businesses')
      .update({
        twilio_call_forwarding_enabled: callForwardingEnabled,
        twilio_forwarding_number: callForwardingEnabled ? formatToE164(forwardingNumber) : null
      })
      .eq('id', businessId);

    if (error) throw error;

    return {
      success: true
    };
  } catch (error) {
    console.error('Error updating call forwarding settings:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update call forwarding settings'
    };
  }
}

/**
 * Automatically configure Twilio webhooks for a business
 */
export async function configureTwilioWebhooks({
  businessId,
  accountSid,
  authToken,
  phoneNumber
}: {
  businessId: string;
  accountSid: string;
  authToken: string;
  phoneNumber: string;
}): Promise<{ success: boolean; error?: string; voiceUrl?: string; statusCallbackUrl?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    // Get the current session for authentication
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    if (!accessToken) {
      throw new Error('No authentication token available');
    }

    console.log('🔧 Configuring Twilio webhooks automatically...');

    // Call our edge function to configure the webhooks using Supabase client
    const { data: result, error: functionError } = await supabase.functions.invoke('configure-twilio-webhooks', {
      body: {
        business_id: businessId,
        account_sid: accountSid,
        auth_token: authToken,
        phone_number: phoneNumber
      }
    });

    if (functionError) {
      console.error('❌ Failed to configure webhooks:', functionError);
      return {
        success: false,
        error: functionError.message || 'Failed to configure Twilio webhooks'
      };
    }

    if (!result || !result.success) {
      console.error('❌ Failed to configure webhooks:', result);
      return {
        success: false,
        error: result?.error || 'Failed to configure Twilio webhooks'
      };
    }

    console.log('✅ Twilio webhooks configured successfully');
    return {
      success: true,
      voiceUrl: result.voiceUrl,
      statusCallbackUrl: result.statusCallbackUrl
    };
  } catch (error) {
    console.error('Error configuring Twilio webhooks:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to configure Twilio webhooks'
    };
  }
}

/**
 * Disconnect and reset Twilio integration for a business
 */
export async function disconnectTwilio({
  businessId
}: {
  businessId: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    console.log('🔌 Disconnecting Twilio integration...');

    // Reset all Twilio-related fields in the database
    const { error } = await supabase
      .from('businesses')
      .update({
        twilio_enabled: false,
        twilio_account_sid: null,
        twilio_auth_token: null,
        VITE_TWILIO_PHONE_NUMBER: null,
        twilio_status: null,
        twilio_error_message: null,
        twilio_call_forwarding_enabled: false,
        twilio_forwarding_number: null,
        twilio_webhooks_configured: false,
        twilio_webhook_configured_at: null
      })
      .eq('id', businessId);

    if (error) throw error;

    console.log('✅ Twilio integration disconnected successfully');
    return {
      success: true
    };
  } catch (error) {
    console.error('Error disconnecting Twilio:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to disconnect Twilio'
    };
  }
}